/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { lazy } from "react";
import keys from "configs/constants";

// Lazy load route components for code splitting
const Dashboard = lazy(() => import("views/Dashboard"));
const Sites = lazy(() => import("views/Sites"));
const Devices = lazy(() => import("views/Devices"));
const SingleView = lazy(() => import("views/SingleView"));
const RegisterThing = lazy(() => import("views/RegisterThing"));
const EditSiteProperties = lazy(() => import("views/EditSiteProperties"));
const EditGatewayProperties = lazy(() => import("views/EditGatewayProperties"));
const Reports = lazy(() => import("views/Reports"));
const AlarmLog = lazy(() => import("views/AlarmLog"));
const Permissions = lazy(() => import("views/Permissions"));
const License = lazy(() => import("views/admin/License"));
const MaintenanceWindows = lazy(() => import("views/admin/MaintenanceWindows"));
const ExternalLink = lazy(() => import("views/ExternalLink"));
const UserWallet = lazy(() => import("./views/UserWallet"));
const FleetView = lazy(() => import("views/FleetView"));
const ApiView = lazy(() => import("views/Api"));
const Roles = lazy(() => import("views/Roles"));
const AuditLog = lazy(() => import("views/AuditLog"));
const InfoPrivacyTerms = lazy(() => import("views/InfoPrivacyTerms"));

export const isUserMenuRoute = (route) => {
  return (
    routes.some((r) => route.includes(r.path) && r?.menu === true)
  );
};

const routes = [
  {
    path: "/index",
    name: "dashboard",
    icon: keys.ICON_MENU_DASHBOARD,
    component: Dashboard,
    layout: "/std",
  },
  {
    // In the sidebar only with CS_49661_SPLIT_DASHBOARD (see Sidebar).
    path: "/sites",
    name: "sites",
    icon: keys.ICON_MENU_SITE,
    component: Sites,
    layout: "/std",
  },
  {
    path: "/alrlg",
    name: "alarmLog",
    icon: keys.ICON_MENU_ALARM_LOG,
    component: AlarmLog,
    layout: "/std",
    menu: true,
    nActiveAlarms: true,
  },
  {
    path: "/fleet",
    name: "fleet",
    icon: keys.ICON_MENU_FLEET,
    component: FleetView,
    layout: "/std",
    menu: true,
  },
  {
    path: "/dvcs",
    name: "myConnectedControls",
    icon: keys.ICON_MENU_CONTROLLER,
    component: Devices,
    layout: "/std",
    menu: false,
  },
  {
    path: "/sstgs",
    name: "editInstallationProperties",
    icon: keys.ICON_MENU_SITE,
    component: EditSiteProperties,
    layout: "/std",
    menu: false,
  },
  {
    path: "/sv",
    name: "connectedControlView",
    icon: keys.ICON_MENU_CONTROLLER,
    component: SingleView,
    layout: "/std",
    menu: false,
  },
  {
    // The sidebar links it itself, only for whoever may read the audit log.
    // Not under "/ad…": azureADB2C sends non-admins away from any path
    // containing "/ad", the admin pages' prefix.
    path: "/audlg",
    name: "auditLog",
    icon: keys.ICON_MENU_AUDIT_LOG,
    component: AuditLog,
    layout: "/std",
    menu: false,
  },
  {
    path: "/gtw",
    name: "editGatewayProperties",
    icon: keys.ICON_CONTROLLER,
    component: EditGatewayProperties,
    layout: "/std",
    menu: false,
  },
  {
    path: "/rpts",
    name: "reports",
    icon: keys.ICON_REPORTS,
    component: Reports,
    layout: "/std",
    menu: false,
  },
  {
    // Reached from the button in Access Rights, not from the sidebar: it answers
    // a question asked there rather than being a place of its own to visit.
    path: "/roles",
    name: "roles",
    icon: keys.ICON_MENU_VISIBILITY,
    component: Roles,
    layout: "/std",
    menu: false,
  },
  {
    // The sidebar shows these documents in a dialog; this page is for direct
    // links to them, such as the one on Register New Gateway.
    path: "/info",
    name: "infoPrivacyTerms",
    icon: keys.ICON_INFO,
    component: InfoPrivacyTerms,
    layout: "/std",
    menu: false,
  },
  {
    path: "/prmssns",
    name: "accessRights",
    icon: keys.ICON_MENU_VISIBILITY,
    component: Permissions,
    layout: "/std",
    menu: true,
    section: "configuration",
  },
  {
    path: "/lic",
    name: "Wallet",
    icon: keys.ICON_MENU_LICENSE,
    component: UserWallet,
    layout: "/std",
    menu: true,
    section: "configuration",
    license: true,
  },
  {
    path: "/api",
    name: "api",
    icon: keys.ICON_MENU_API,
    component: ApiView,
    layout: "/std",
    menu: true,
    section: "configuration",
  },
  {
    path: "",
    name: "registerNewGateway",
    icon: keys.ICON_MENU_ADD_GATEWAY,
    component: RegisterThing,
    menu: false,
    layout: "/tr",
  },
  {
    path: "/license",
    name: "license",
    icon: keys.ICON_MENU_LICENSE,
    component: License,
    layout: "/ad",
    superUser: true,
  },
  {
    path: "/maintenance-windows",
    name: "maintenanceWindows",
    icon: keys.ICON_MENU_MAINTENANCE,
    component: MaintenanceWindows,
    layout: "/ad",
    superUser: true,
  },
  {
    path: "/",
    name: "externalLink",
    component: ExternalLink,
    layout: "/ext",
    menu: false,
  },
];
export default routes;
